import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type {
	FormPurpose,
	FormRuleModel,
	FormSettingsModel,
} from '../../forms/form-types';
import { BaseEntity } from './base.entity';
import { PortalSiteEntity } from './portal-site.entity';
import { UserEntity } from './user.entity';

/**
 * A tenant-defined form (Workd Forms, docs/claude/forms/01-design.md §2): a contact form,
 * the storefront registration form, or any other form a brand's websites submit to.
 *
 * `formKey` is unique per brand, enforced in the service rather than by a unique index
 * because MySQL treats NULL `fk_portal_site__id` values (tenant-wide forms) as distinct.
 * The key never changes after the first submission.
 */
@Index('idx_form_portal_site_key', ['fkPortalSiteId', 'formKey'], {})
@Index('idx_form_purpose', ['purpose'], {})
@Entity('form')
export class FormEntity extends BaseEntity {
	// The brand. Null = tenant-wide; a request looks for the brand's form first, then this.
	@Column('int', { name: 'fk_portal_site__id', nullable: true, unsigned: true })
	public fkPortalSiteId!: number | null;

	@Column('varchar', { name: 'formKey', length: 32 })
	public formKey!: string;

	@Column('varchar', { name: 'name', length: 120 })
	public name!: string;

	@Column('varchar', { name: 'purpose', length: 20, default: 'custom' })
	public purpose!: FormPurpose;

	// Internal note for staff; never sent to a page.
	@Column('varchar', { name: 'description', nullable: true, length: 500 })
	public description!: string | null;

	@Column('varchar', { name: 'successMessage', nullable: true, length: 500 })
	public successMessage!: string | null;

	// The language the form is written in. Tenant-authored labels are data, not t() keys.
	@Column('varchar', { name: 'locale', length: 10, default: 'en' })
	public locale!: string;

	// Bumped whenever the fields or rules change; submissions point at form_version rows.
	@Column('int', {
		name: 'currentVersion',
		unsigned: true,
		default: () => "'0'",
	})
	public currentVersion!: number;

	// Cross-field rules such as "at least one of" (email or phone).
	@Column('json', { name: 'fieldRules', nullable: true })
	public fieldRules!: Array<FormRuleModel> | null;

	// An inactive form answers exactly like an unknown one, so keys cannot be probed.
	@Column({ type: 'boolean', name: 'isActive', default: true })
	public isActive!: boolean;

	// Tenant-local user who owns new submissions when no matched rep applies.
	@Column('int', {
		name: 'fk_user__id_defaultAssignee',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdDefaultAssignee!: number | null;

	// Spam protection, rate limit, duplicate window and blocklist (FormSettingsModel).
	@Column('json', { name: 'settings', nullable: true })
	public settings!: FormSettingsModel | null;

	@ManyToOne(() => PortalSiteEntity, {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([{ name: 'fk_portal_site__id', referencedColumnName: 'id' }])
	public portalSite!: PortalSiteEntity | null;

	@ManyToOne(() => UserEntity, {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([
		{ name: 'fk_user__id_defaultAssignee', referencedColumnName: 'id' },
	])
	public defaultAssignee!: UserEntity | null;
}
